import { useEffect, useState } from "react";
import axios from "axios";
import { Facebook, X, Loader2, Check, ExternalLink, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Admin-only "Post to Facebook" modal.
 *
 *  Pre-fills caption from the story title + public URL, and image
 *  from the story's cover/thumbnail. Operator can edit either before
 *  clicking "Post Now". On success the dialog flips to a "Posted ✓"
 *  state with a link to the live FB post.
 *
 *  Requires `FB_PAGE_ID` + `FB_PAGE_ACCESS_TOKEN` to be set in the
 *  backend .env. If they're not, the modal shows a clear "configure
 *  first" hint instead of letting the operator click into a 409.
 *
 *  Admin auth: reuses the `storylo_admin_token` localStorage key the
 *  rest of the Studio uses.
 */
export default function FacebookShareModal({ video, onClose }) {
  const [adminToken] = useState(
    () => (typeof window !== "undefined"
      && window.localStorage.getItem("storylo_admin_token")) || ""
  );
  const [caption, setCaption] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [includeLink, setIncludeLink] = useState(true);
  const [status, setStatus] = useState({ loading: true });
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(null);   // { permalink, id }
  const [error, setError] = useState("");

  // Resolve sensible defaults synchronously so the modal renders fast.
  useEffect(() => {
    if (!video) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const slug = video.slug || video.id;
    const storyUrl = `${origin}/api/share/${slug}`;
    const title = video.title || "A Storylo story";
    setCaption(`${title}\n\n${storyUrl}`);
    // Prefer share_card (a 1200×630 hand-picked one), fall back to the
    // auto thumbnail. Both are relative paths — make them absolute so
    // the editable text-field shows the full URL the operator can sanity
    // check before posting.
    const rawImg = video.share_card || video.thumbnail_url || "";
    const absImg = rawImg.startsWith("http")
      ? rawImg
      : (rawImg ? `${origin}${rawImg}` : "");
    setImageUrl(absImg);
  }, [video]);

  // Check FB configuration status once the modal opens.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/facebook/status`, {
          headers: { "X-Admin-Token": adminToken },
        });
        if (!cancelled) setStatus({ loading: false, ...r.data });
      } catch (e) {
        if (!cancelled) {
          setStatus({
            loading: false,
            configured: false,
            error: e?.response?.data?.detail || e.message,
          });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken]);

  const postNow = async () => {
    if (!caption.trim()) { setError("Caption can't be empty."); return; }
    if (!imageUrl.trim()) { setError("Image URL can't be empty."); return; }
    setError("");
    setPosting(true);
    try {
      const r = await axios.post(
        `${API}/admin/share/facebook/${video.id}`,
        {
          caption: caption,
          image_url: imageUrl,
          include_link: includeLink,
        },
        { headers: { "X-Admin-Token": adminToken } },
      );
      setPosted({
        id: r.data.facebook_post_id,
        permalink: r.data.permalink,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Post failed");
    } finally {
      setPosting(false);
    }
  };

  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="fb-share-modal-backdrop"
    >
      <div
        className="w-full max-w-xl bg-[color:var(--panel)] border border-white/10 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="fb-share-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center">
              <Facebook className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold">Post to Facebook</div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                Auto-post · your Page
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-white/5 flex items-center justify-center"
            data-testid="fb-share-modal-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {status.loading ? (
            <div className="flex items-center gap-2 text-sm text-[color:var(--muted)]">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking Facebook configuration…
            </div>
          ) : !status.configured ? (
            <ConfigMissing status={status} />
          ) : posted ? (
            <PostSuccess posted={posted} onClose={onClose} />
          ) : (
            <>
              {/* Image preview */}
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-2">
                  Cover image
                </div>
                <div className="aspect-[1200/630] w-full rounded-lg overflow-hidden bg-black border border-white/5 mb-2">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="Cover preview for Facebook post"
                      className="w-full h-full object-cover"
                      data-testid="fb-share-image-preview"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[color:var(--muted)] text-xs">
                      No cover image
                    </div>
                  )}
                </div>
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="input text-xs w-full"
                  placeholder="https://…/cover.jpg"
                  data-testid="fb-share-image-url"
                />
              </div>

              {/* Caption */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                    Caption
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] text-[color:var(--muted)] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeLink}
                      onChange={(e) => setIncludeLink(e.target.checked)}
                      data-testid="fb-share-include-link"
                    />
                    Include story link
                  </label>
                </div>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  rows={5}
                  className="input text-sm w-full font-normal leading-relaxed"
                  placeholder="Caption text…"
                  data-testid="fb-share-caption"
                />
                <div className="text-[10px] text-[color:var(--muted)] mt-1">
                  {caption.length} characters
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-200">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span data-testid="fb-share-error">{error}</span>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  onClick={postNow}
                  disabled={posting}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="fb-share-post-now"
                >
                  {posting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Posting…</>
                  ) : (
                    <><Facebook className="w-4 h-4" /> Post Now</>
                  )}
                </button>
                <button
                  onClick={onClose}
                  className="btn-ghost text-sm"
                  data-testid="fb-share-cancel"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function ConfigMissing({ status }) {
  return (
    <div className="space-y-3" data-testid="fb-share-config-missing">
      <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-100">
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold mb-1">Facebook isn't configured yet</div>
          <div className="text-yellow-100/80">
            {status.error
              ? status.error
              : "The backend is missing FB_PAGE_ID and/or FB_PAGE_ACCESS_TOKEN."}
          </div>
        </div>
      </div>
      <div className="text-xs text-[color:var(--muted)] leading-relaxed">
        <div className="font-semibold text-white mb-2">One-time setup (≈10 min):</div>
        <ol className="space-y-1.5 list-decimal pl-5">
          <li>Go to <a className="underline hover:text-[color:var(--accent)]"
                href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer">
                developers.facebook.com/apps</a> → create a Facebook App.</li>
          <li>From your Page → About → copy the numeric <strong>Page ID</strong>.</li>
          <li>Open the <a className="underline hover:text-[color:var(--accent)]"
                href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer">
                Graph API Explorer</a> → "Get Page Access Token" → tick <code>pages_manage_posts</code> →
                paste the resulting token into <code>FB_PAGE_ACCESS_TOKEN</code>.</li>
          <li>Extend the token via the <a className="underline hover:text-[color:var(--accent)]"
                href="https://developers.facebook.com/tools/debug/accesstoken/" target="_blank" rel="noreferrer">
                Access Token Debugger</a> so it doesn't expire.</li>
          <li>Add <code>FB_PAGE_ID=…</code> and <code>FB_PAGE_ACCESS_TOKEN=…</code> to your backend
                environment variables, then Save to GitHub → Redeploy.</li>
        </ol>
        <div className="mt-3">
          Full guide:{" "}
          <a
            href="https://www.storylo.co.uk/SETUP_FACEBOOK.md"
            target="_blank" rel="noreferrer"
            className="underline hover:text-[color:var(--accent)] inline-flex items-center gap-1"
          >
            SETUP_FACEBOOK.md <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}


function PostSuccess({ posted, onClose }) {
  return (
    <div className="space-y-4 py-4 text-center" data-testid="fb-share-success">
      <div className="w-16 h-16 mx-auto rounded-full bg-green-500/15 border border-green-500/30 flex items-center justify-center">
        <Check className="w-7 h-7 text-green-400" />
      </div>
      <div className="space-y-1">
        <div className="text-base font-semibold">Posted to Facebook</div>
        <div className="text-xs text-[color:var(--muted)]">
          Your story is now live on the Page feed.
        </div>
      </div>
      <div className="flex items-center justify-center gap-3 pt-1">
        {posted.permalink && (
          <a
            href={posted.permalink}
            target="_blank"
            rel="noreferrer"
            className="btn-primary text-sm inline-flex items-center gap-2"
            data-testid="fb-share-view-on-facebook"
          >
            View on Facebook <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
        <button
          onClick={onClose}
          className="btn-ghost text-sm"
          data-testid="fb-share-done"
        >
          Done
        </button>
      </div>
    </div>
  );
}
